import React from "react";

import { IconUpload, InfoTooltip, RefSelect, TagInput } from "../BuilderUtils";
import { authoredDecisionIds } from "../measurePool";
import { doughnutScoreOptionsOf, findDecision, scoreOptionsOf } from "./common";
import { CHART_DISPLAYS, chartDisplayOf, chartScoreIdsOf } from "../../interaction_pages/commandControlCharts";

/**
 * CE QUE LA PAGE MESURE.
 *
 * Trois choses distinctes, qui étaient dispersées :
 *  - les scores portés par chaque carte de décision (le coût en €, les points
 *    de vote…), avec leur icône et, éventuellement, une règle de validation ;
 *  - le score comparé dans l'anneau, pour les pages commande & contrôle ;
 *  - les indicateurs de tendance, purement informatifs.
 *
 * LA RÈGLE DE VALIDATION VIT DANS LA LIGNE DU SCORE parce qu'elle porte sur lui
 * : le seuil est un champ du même objet. La sortir dans un onglet « Règles »
 * aurait obligé à nommer deux fois le score dont on parle.
 *
 * L'APERÇU MONTRE UNE VRAIE DÉCISION de la page — la première qui a un impact
 * sur ce score. Un gabarit avec un nombre inventé ne dit pas si les icônes
 * répétées déborderont de la carte ; celui-ci le dit, et le signale.
 */
export default function ScoresInspector({ page, onPatchContent, ctx }) {
  const { scoreDic, token } = ctx;
  const scoreOptions = scoreOptionsOf(scoreDic);
  const doughnutOptions = doughnutScoreOptionsOf(scoreDic);
  const list = page.content?.display_score_decisions || [];
  const decisionIds = authoredDecisionIds(page, ctx.config);

  // LA REMISE À ZÉRO EST UN RÉGLAGE DE LA PAGE (`content.score_resets`), pas de
  // la ligne d'affichage : le serveur l'applique en entrée de page, que le
  // score soit affiché ou non. Elle est éditée ICI, dans la ligne du score,
  // pour la même raison que le seuil : elle porte sur lui. Une ligne qui
  // disparaît emporte sa remise à zéro — un réglage qu'aucun écran ne montre
  // plus n'a pas à continuer d'agir en jeu.
  const resets = page.content?.score_resets || [];
  const resetOf = (scoreId) => resets.find((r) => r.score_id === scoreId);
  const withoutReset = (scoreId) => resets.filter((r) => r.score_id !== scoreId);
  const setReset = (scoreId, value) =>
    onPatchContent({ score_resets: [...withoutReset(scoreId), { score_id: scoreId, value }] });
  const clearReset = (scoreId) => {
    const next = withoutReset(scoreId);
    onPatchContent({ score_resets: next.length ? next : undefined });
  };

  const patchList = (next) => onPatchContent({ display_score_decisions: next });
  const updateAt = (idx, fields) => patchList(list.map((row, i) => (i === idx ? { ...row, ...fields } : row)));
  const removeAt = (idx) => {
    const removed = list[idx]?.score_id;
    const next = list.filter((_, i) => i !== idx);
    const stillShown = removed && next.some((row) => row.score_id === removed);
    if (removed && !stillShown && resetOf(removed)) {
      onPatchContent({ display_score_decisions: next, score_resets: withoutReset(removed).length ? withoutReset(removed) : undefined });
    } else {
      patchList(next);
    }
  };

  // LE GRAPHE D'UNE PAGE COMMANDE & CONTRÔLE : un score, ou deux. Un seul
  // s'écrit comme avant (`score_in_doughnut`, sans `chart_scores`) — la page
  // reste lisible par tout ce qui ne connaît que ce champ. Deux s'écrivent dans
  // `chart_scores`, `score_in_doughnut` restant le premier.
  const chartIds = chartScoreIdsOf(page.content);
  const setChartIds = (ids) => {
    const unique = [...new Set(ids.filter(Boolean))];
    onPatchContent({
      score_in_doughnut: unique[0] || "",
      chart_scores: unique.length > 1 ? unique : undefined,
    });
  };
  const chartDisplay = chartDisplayOf(page.content);

  const addRow = () =>
    patchList([...list, { score_id: "", icon_url: "", display_type: "value", threshold: undefined }]);

  // Choisir un score remplit l'icône depuis le score_dic tant qu'aucune n'a été
  // posée : dans l'immense majorité des cas c'est celle qu'on voulait.
  const selectScoreId = (idx, scoreId) => {
    const current = list[idx] || {};
    updateAt(idx, { score_id: scoreId, icon_url: current.icon_url || scoreDic?.[scoreId]?.icon || "" });
    if (current.score_id && current.score_id !== scoreId && resetOf(current.score_id)) clearReset(current.score_id);
  };

  return (
    <>
      {page.type === "command_control" && (
        <div style={{ marginBottom: 20 }}>
          <label className="builder-label">
            <span className="field-label-row">
              Score du graphe
              <InfoTooltip text="Score affiché en temps réel dans le graphe. Doit avoir une structure metadata {current, standard} (ex: chantier-waterfootprint)." />
            </span>
            <RefSelect
              value={chartIds[0] || ""}
              onChange={(v) => setChartIds([v, chartIds[1]])}
              options={doughnutOptions}
              placeholder="Choisir un score (avec metadata standard/current)…"
            />
          </label>
          <label className="builder-label">
            <span className="field-label-row">
              Second score (facultatif)
              <InfoTooltip text="Un second graphe sous le premier, avec son titre (le nom du score) et sa réduction. Les mesures doivent ventiler leur impact sur ce score par catégorie, sinon son graphe ne bougera pas." />
            </span>
            <RefSelect
              value={chartIds[1] || ""}
              onChange={(v) => setChartIds([chartIds[0], v])}
              options={doughnutOptions.filter((o) => o.value !== chartIds[0])}
              placeholder="Aucun — un seul graphe"
            />
          </label>
          <div className="builder-inline-row builder-display-type-row">
            <span className="builder-label-text">Forme :</span>
            <div className="builder-toggle-group">
              {CHART_DISPLAYS.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  className={`builder-toggle-btn ${chartDisplay === option.id ? "active" : ""}`}
                  onClick={() => onPatchContent({ chart_display: option.id === "doughnut" ? undefined : option.id })}
                >{option.label}</button>
              ))}
            </div>
          </div>
          {chartIds.length > 1 && chartDisplay === "doughnut" && (
            <div className="builder-hint">
              Deux anneaux entiers se partagent la hauteur d'un seul : les barres ou les demi-anneaux restent plus lisibles à deux scores.
            </div>
          )}
        </div>
      )}

      <div className="builder-section-header">
        <h3 className="builder-subsection-title">
          <span className="field-label-row">
            Scores affichés sur chaque décision ({list.length})
            <InfoTooltip text="Score(s) dont la valeur d'impact est affichée sur les cartes de décision (ex: le coût en €, ou les points de vote). Chaque score peut aussi porter une règle bloquant la validation." />
          </span>
        </h3>
        <button className="builder-btn-secondary" onClick={addRow}>+ Ajouter</button>
      </div>

      {list.length === 0 && (
        <div className="builder-empty">Aucun score affiché sur les décisions.</div>
      )}

      {list.map((ds, idx) => {
        const scoreIcon = scoreDic?.[ds.score_id]?.icon;
        const iconMismatch = scoreIcon && ds.icon_url && ds.icon_url !== scoreIcon;
        const scoreName = scoreDic?.[ds.score_id]?.name || ds.score_id || "ce score";
        const hasConstraint = ds.threshold !== undefined && ds.threshold !== "";
        const reset = ds.score_id ? resetOf(ds.score_id) : undefined;
        const initialValue = scoreDic?.[ds.score_id]?.value ?? 0;

        const sampleDec = decisionIds
          .map((decId) => findDecision({ ...ctx, page }, decId))
          .find((dec) => dec?.score?.[ds.score_id] !== undefined);
        const sampleImpact = sampleDec?.score?.[ds.score_id]?.impact;
        // Une page commande & contrôle affiche une RÉDUCTION : l'impact y est
        // négatif, et c'est sa valeur retournée qu'on montre.
        const previewValue = page.type === "command_control"
          ? (sampleImpact ?? 0) * -1
          : Math.abs(sampleImpact ?? 0);

        return (
          <div key={idx} className="builder-display-score-row">
            <div className="builder-inline-row">
              <RefSelect
                value={ds.score_id}
                onChange={(v) => selectScoreId(idx, v)}
                options={scoreOptions}
                placeholder="score_id"
              />
              <IconUpload
                value={ds.icon_url}
                token={token}
                onChange={(url) => updateAt(idx, { icon_url: url })}
                title="Icône affichée sur la carte"
              />
              {scoreIcon && iconMismatch && (
                <button
                  type="button"
                  className="builder-icon-btn"
                  title="Reprendre l'icône du score"
                  onClick={() => updateAt(idx, { icon_url: scoreIcon })}
                >↺</button>
              )}
              <button
                type="button"
                className="builder-remove-row-btn"
                title="Supprimer ce score de la liste"
                onClick={() => removeAt(idx)}
              >🗑 Supprimer ce score</button>
            </div>

            <div className="builder-inline-row builder-display-type-row">
              <span className="builder-label-text">Affichage :</span>
              <div className="builder-toggle-group">
                <button
                  type="button"
                  className={`builder-toggle-btn ${(ds.display_type || "value") === "value" ? "active" : ""}`}
                  onClick={() => updateAt(idx, { display_type: "value" })}
                >123 Valeur + icône</button>
                <button
                  type="button"
                  className={`builder-toggle-btn ${ds.display_type === "multiple_icon" ? "active" : ""}`}
                  onClick={() => updateAt(idx, { display_type: "multiple_icon" })}
                >●●● Icônes répétées</button>
              </div>
              {ds.display_type === "multiple_icon" && Math.abs(previewValue) > 8 && (
                <span className="builder-display-score-warning">
                  ⚠ impact jusqu'à {Math.abs(previewValue)} sur cette page : risque de déborder la carte
                </span>
              )}
            </div>

            <div className="builder-constraint-block">
              <label className="builder-constraint-toggle">
                <input
                  type="checkbox"
                  checked={hasConstraint}
                  onChange={(e) => updateAt(idx, { threshold: e.target.checked ? 0 : undefined })}
                />
                <span>Bloquer la validation si {scoreName} descend trop bas</span>
                <InfoTooltip text="Pendant la sélection, le total cumulé des décisions cochées est suivi en direct pour ce score. Si ce total passe sous le seuil défini, le bouton 'Valider' de la page se désactive tant que le joueur n'a pas retiré des décisions pour repasser au-dessus." />
              </label>
              {hasConstraint && (
                <div className="builder-constraint-detail">
                  <span className="builder-label-text">Seuil minimum :</span>
                  <input
                    className="builder-input small"
                    type="number"
                    value={ds.threshold ?? 0}
                    onChange={(e) => updateAt(idx, { threshold: e.target.value !== "" ? Number(e.target.value) : 0 })}
                  />
                  <span className="builder-constraint-example">
                    Ex : si {scoreName} démarre à 0 et que les décisions cochées cumulent moins de {ds.threshold ?? 0}, « Valider » restera désactivé.
                  </span>
                </div>
              )}
            </div>

            <div className="builder-constraint-block">
              <label className="builder-constraint-toggle">
                <input
                  type="checkbox"
                  checked={!!reset}
                  disabled={!ds.score_id}
                  onChange={(e) => (e.target.checked ? setReset(ds.score_id, initialValue) : clearReset(ds.score_id))}
                />
                <span>Repartir d'une valeur fixe quand la page s'ouvre</span>
                <InfoTooltip text="Quand cette page arrive à l'écran, le score est remis à la valeur indiquée, quoi que les pages précédentes y aient ajouté ou retiré. Les impacts des décisions cochées ici s'appliquent ensuite par-dessus. Utile pour un compteur « à la page » — par exemple les économies d'eau à court terme d'un tour Sécheresse, qui n'ont pas à porter ce que les tours précédents ont laissé. Appliqué une fois par passage sur la page : recharger la page en cours de tour ne remet rien." />
              </label>
              {reset && (
                <div className="builder-constraint-detail">
                  <span className="builder-label-text">Valeur de départ :</span>
                  <input
                    className="builder-input small"
                    type="number"
                    value={reset.value ?? 0}
                    onChange={(e) => setReset(ds.score_id, e.target.value !== "" ? Number(e.target.value) : 0)}
                  />
                  <span className="builder-constraint-example">
                    {reset.value === initialValue
                      ? `C'est la valeur initiale de ${scoreName} dans l'atelier.`
                      : `La valeur initiale de ${scoreName} dans l'atelier est ${initialValue}.`}
                  </span>
                </div>
              )}
            </div>

            <div className="builder-display-score-preview">
              <span className="builder-display-score-preview-label">
                Aperçu {sampleDec ? `(sur « ${sampleDec.title} »)` : ""} :
              </span>
              {!ds.score_id ? (
                <span className="builder-empty-inline">choisissez un score</span>
              ) : !sampleDec ? (
                <span className="builder-empty-inline">aucune décision de la liste n'a d'impact sur ce score</span>
              ) : ds.display_type === "multiple_icon" ? (
                <div className="mosaic-multiple-icons">
                  {Array.from({ length: Math.abs(previewValue) }).map((_, i) => (
                    <img key={i} className="mosaic-decision-score-icon" src={ds.icon_url} alt="" />
                  ))}
                </div>
              ) : (
                <div className="mosaic-decision-score-container">
                  <div>{previewValue}</div>
                  <img className="mosaic-decision-score-icon" src={ds.icon_url} alt="" />
                </div>
              )}
            </div>
          </div>
        );
      })}

      <div style={{ marginTop: 22 }}>
        <div className="field-label-row" style={{ marginBottom: 8 }}>
          <span className="builder-label-text">Indicateurs additionnels (radar)</span>
          <InfoTooltip text="Scores affichés sous forme d'icône + flèche (hausse/baisse) en bas de chaque carte de décision. Purement informatif, n'affecte pas le jeu. L'icône vient automatiquement du score (score_dic)." />
        </div>
        {page.type === "command_control" && (
          <div className="builder-warning-inline">
            ⚠ Ce champ n'est actuellement pas affiché sur les pages de type « command_control » — la configuration sera enregistrée mais restera invisible en jeu.
          </div>
        )}
        <TagInput
          tags={page.content?.list_additional_indicators || []}
          onChange={(v) => onPatchContent({ list_additional_indicators: v })}
          suggestions={scoreOptions.map((o) => o.value)}
        />
        {(page.content?.list_additional_indicators || []).length > 0 && (
          <div className="builder-indicator-preview-row">
            <span className="builder-display-score-preview-label">Aperçu :</span>
            {(page.content.list_additional_indicators || []).map((indicatorId) => {
              const scoreDef = scoreDic?.[indicatorId];
              const sampleDec = decisionIds
                .map((decId) => findDecision({ ...ctx, page }, decId))
                .find((dec) => dec?.score?.[indicatorId]?.impact);
              const impact = sampleDec?.score?.[indicatorId]?.impact;
              return (
                <div key={indicatorId} className="builder-indicator-preview-item" title={scoreDef?.name || indicatorId}>
                  <img src={scoreDef?.icon || "/default-icon.png"} alt="" className="mosaic-decision-indicator-icon" />
                  {impact !== undefined && impact !== 0 && (
                    <span className={`builder-indicator-preview-arrow ${impact > 0 ? "up" : "down"}`}>
                      {impact > 0 ? "▲" : "▼"}
                    </span>
                  )}
                  <span className="builder-indicator-preview-name">{scoreDef?.name || indicatorId}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
